import { useEffect, useRef, useState } from 'react';
import { fetchChartRollup } from '../api/dataApi';
import type { ChartEntry } from './chartBuckets';
import type { Message } from '../types';

export interface RollupData {
  entries: ChartEntry[];
  // Ids of every message the store already held when the rollup arrived -
  // the server's numbers include those, so the chart must not count them
  // again when folding in live signals (see SignalActivityChart).
  covered: Set<string>;
  asOf: number;
}

// Module-level so a channel's last rollup survives tab switches and
// remounts: reopening the Charts panel paints instantly from this cache
// while a fresh fetch revalidates in the background.
const cache = new Map<string, RollupData>();
// Bounded (least-recently-set first out): each entry holds a whole channel's
// slot list, so an unbounded map grows with every channel/interval visited.
const MAX_CACHED_ROLLUPS = 12;

// Server rollups for one channel: the Charts panel's first frame, painted
// from pre-aggregated cells instead of re-bucketing loaded messages. `tier`
// "m" = 1-minute slots (Hours view), "q" = 15-minute slots. `fromMs` 0 =
// whole history. Returns null until the first rollup is available - callers
// then fall back to computing from messages - and stays null if the
// endpoint is unreachable/forbidden.
// How often an open chart re-reads its rollup. Live signals fold in from the
// message feed in between, but that feed is a bounded window - a busy channel
// can outrun it - so the server's numbers are re-pulled to keep the chart
// exact. The minute tier moves fastest.
const REFRESH_MS: Record<'m' | 'q', number> = { m: 30_000, q: 60_000 };

export interface RollupState {
  /** The rollup for EXACTLY the requested window - never a previous window's. */
  data: RollupData | null;
  /** True until the first answer (or failure) for the requested window; callers show a loading state, not partial message data. */
  loading: boolean;
}

export function useChartRollup(
  channelId: string | undefined,
  tier: 'm' | 'q',
  fromMs: number,
  messages: Message[]
): RollupState {
  const key = channelId ? `${channelId}|${tier}|${fromMs}` : '';
  // State is tagged with the key it was loaded for. Reading it back only when
  // the tag matches the CURRENT key is what stops a span change from painting
  // the previous span's rollup against the new span's buckets for a frame.
  const [loaded, setLoaded] = useState<{ key: string; data: RollupData | null; failed: boolean }>(() => ({
    key,
    data: key ? cache.get(key) ?? null : null,
    failed: false,
  }));
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    setLoaded((prev) => (prev.key === key ? prev : { key, data: cache.get(key) ?? null, failed: false }));

    const load = () =>
      fetchChartRollup(channelId, tier, fromMs / 1000).then((r) => {
        if (cancelled) return;
        if (!r) {
          // Endpoint unreachable/forbidden: keep whatever we have; if nothing, let the chart fall back to messages.
          setLoaded((prev) => (prev.key === key ? { ...prev, failed: prev.data === null } : prev));
          return;
        }
        const entries: ChartEntry[] = [];
        for (const s of r.series) {
          for (const [t, count, sum, min, max, last, lastAt, nval] of s.slots) {
            entries.push({ type: s.type, t: t * 1000, count, sum, min, max, last, lastAt, nval });
          }
        }
        const next: RollupData = { entries, covered: new Set(messagesRef.current.map((m) => m.id)), asOf: r.asOf };
        cache.delete(key);
        cache.set(key, next);
        while (cache.size > MAX_CACHED_ROLLUPS) cache.delete(cache.keys().next().value as string);
        setLoaded({ key, data: next, failed: false });
      });

    void load();
    const timer = setInterval(() => void load(), REFRESH_MS[tier]);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [channelId, key, tier, fromMs]);

  if (!channelId) return { data: null, loading: false };
  const current = loaded.key === key ? loaded : { key, data: cache.get(key) ?? null, failed: false };
  return { data: current.data, loading: current.data === null && !current.failed };
}
